import {
  DeleteOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Button, Divider, List, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type { LatLonPoint } from '@/types/barriers';

interface WaypointsEditorProps {
  readonly waypoints: readonly LatLonPoint[];
  readonly addingWaypoint: boolean;
  readonly suggesting: boolean;
  readonly canSuggest: boolean;
  readonly onStartAdd: () => void;
  readonly onRemove: (index: number) => void;
  readonly onSuggest: () => void;
}

const LAT_LON_DECIMALS = 5;

export default function WaypointsEditor({
  waypoints,
  addingWaypoint,
  suggesting,
  canSuggest,
  onStartAdd,
  onRemove,
  onSuggest,
}: WaypointsEditorProps) {
  const { t } = useTranslation();

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Typography.Text strong>
        {t('barriers.waypoints.title', { count: waypoints.length })}
      </Typography.Text>

      {waypoints.length > 0 && (
        <List
          size="small"
          bordered
          dataSource={waypoints.map((wp, index) => ({ wp, index }))}
          renderItem={({ wp, index }) => (
            <List.Item
              key={waypointKey(wp, index)}
              actions={[
                <Button
                  key="remove"
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onRemove(index)}
                  aria-label={t('barriers.waypoints.remove')}
                />,
              ]}
            >
              {`${index + 1}. ${formatPoint(wp)}`}
            </List.Item>
          )}
        />
      )}

      <Button
        icon={<PlusOutlined />}
        onClick={onStartAdd}
        disabled={addingWaypoint}
        block
      >
        {addingWaypoint ? t('barriers.waypoints.clickMap') : t('barriers.waypoints.add')}
      </Button>

      <Divider style={{ margin: '8px 0' }} />

      <Button
        icon={<ThunderboltOutlined />}
        onClick={onSuggest}
        loading={suggesting}
        disabled={!canSuggest}
        block
      >
        {t('barriers.waypoints.suggest')}
      </Button>
    </Space>
  );
}

function formatPoint(p: LatLonPoint): string {
  return `${p.lat.toFixed(LAT_LON_DECIMALS)}, ${p.lon.toFixed(LAT_LON_DECIMALS)}`;
}

function waypointKey(p: LatLonPoint, index: number): string {
  // Coordinate-based composite key keeps React node identity stable when the
  // user removes an item in the middle of the list — the index would shift
  // and remount every following row otherwise. Index is appended only as a
  // disambiguator for the rare case of identical coordinates.
  return `${p.lat.toFixed(LAT_LON_DECIMALS)}:${p.lon.toFixed(LAT_LON_DECIMALS)}:${index}`;
}
